import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Users,
  Briefcase,
  FileText,
  Plus,
  ArrowRight,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnterpriseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isCabinet } = useAuth();
  const [enterprise, setEnterprise] = useState(null);
  const [bilans, setBilans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [enterpriseRes, bilansRes] = await Promise.all([
          axios.get(`${API_URL}/enterprises/${id}`),
          axios.get(`${API_URL}/bilans`),
        ]);
        setEnterprise(enterpriseRes.data);
        // Filter bilans for this enterprise
        const enterpriseBilans = bilansRes.data.filter((b) => b.enterprise_id === id);
        setBilans(enterpriseBilans);
      } catch (error) {
        console.error(/* PROD */ "Failed to fetch enterprise:", error);
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getStatusBadge = (status) => {
    const badges = {
      brouillon: "badge-neutral",
      soumis: "badge-warning",
      en_controle: "badge-warning",
      valide: "badge-success",
      rejete: "badge-error",
    };
    const labels = {
      brouillon: "Brouillon",
      soumis: "Soumis",
      en_controle: "En contrôle",
      valide: "Validé",
      rejete: "Rejeté",
    };
    return <span className={badges[status] || "badge-neutral"}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!enterprise) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Entreprise non trouvée</p>
        <Link to="/entreprises">
          <Button className="mt-4">Retour aux entreprises</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enterprise-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/entreprises">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {enterprise.raison_sociale}
          </h1>
          <p className="text-muted-foreground mt-1">
            {enterprise.secteur_activite} • {enterprise.region}
          </p>
        </div>
        {isCabinet && (
          <Button
            className="bg-primary hover:bg-primary/90"
            onClick={() => navigate(`/bilans/nouveau?enterprise=${id}`)}
            data-testid="create-bilan-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Bilan
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading">Informations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {enterprise.email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{enterprise.email}</p>
                  </div>
                </div>
              )}
              {enterprise.telephone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Téléphone</p>
                    <p className="font-medium">{enterprise.telephone}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Adresse</p>
                  <p className="font-medium">{enterprise.adresse}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Effectif global</p>
                  <p className="font-medium">{enterprise.effectif_global} employés</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4">Identifiants légaux</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">RCCM</p>
                  <p className="font-mono font-medium">{enterprise.rccm}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">NIF</p>
                  <p className="font-mono font-medium">{enterprise.nif}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Bilans Sociaux</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-600" />
                <span className="text-sm">Total bilans</span>
              </div>
              <span className="font-bold text-lg">{bilans.length}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
              <span className="text-sm text-emerald-700">Validés</span>
              <span className="font-bold text-emerald-700">
                {bilans.filter((b) => b.status === "valide").length}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
              <span className="text-sm text-amber-700">En attente</span>
              <span className="font-bold text-amber-700">
                {bilans.filter((b) => ["soumis", "en_controle"].includes(b.status)).length}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bilans History */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Historique des Bilans</CardTitle>
          <CardDescription>Tous les bilans sociaux déclarés pour cette entreprise</CardDescription>
        </CardHeader>
        <CardContent>
          {bilans.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-muted-foreground mb-4">Aucun bilan déclaré</p>
              {isCabinet && (
                <Button onClick={() => navigate(`/bilans/nouveau?enterprise=${id}`)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer le premier bilan
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {bilans.map((bilan) => (
                <Link
                  key={bilan.id}
                  to={`/bilans/${bilan.id}`}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Bilan Social {bilan.annee}</p>
                      <p className="text-sm text-slate-500">
                        Créé le {new Date(bilan.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(bilan.status)}
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
